import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JobApplicationModal from '../../components/feature/JobApplicationModal';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function CareersPage() {
  // SEO Configuration
  useSEO({
    title: 'Careers at PURECODE - IT Jobs in Warrington | Software Developer Positions',
    description: 'Join PURECODE Ltd in Warrington! We\'re hiring full stack developers, DevOps engineers, UI/UX designers, and offering internships. Work on cutting-edge fintech projects with competitive salaries and great benefits.',
    keywords: 'IT jobs Warrington, software developer jobs, DevOps engineer, UI/UX designer, tech careers, internships Warrington, fintech jobs',
    canonical: 'https://purecode.uk/careers',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Careers', url: '/careers' }
        ]),
        {
          '@type': 'JobPosting',
          title: 'Senior Full Stack Developer',
          description: 'Experienced full stack developer to join our growing team',
          datePosted: '2024-01-01',
          employmentType: 'FULL_TIME',
          hiringOrganization: {
            '@id': 'https://purecode.uk/#organization'
          },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'The Genesis Centre, Birchwood',
              addressLocality: 'Warrington',
              addressRegion: 'England',
              postalCode: 'WA3 7BH',
              addressCountry: 'GB'
            }
          },
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: 'GBP',
            value: {
              '@type': 'QuantitativeValue',
              minValue: 50000,
              maxValue: 70000,
              unitText: 'YEAR'
            }
          }
        }
      ]
    }
  });

  const [selectedJob, setSelectedJob] = useState<{title: string, department: string} | null>(null);

  const jobs = [
    {
      title: "Software Test Analyst",
      department: "Quality Assurance",
      type: "Full-time",
      location: "Warrington, UK (Hybrid)",
      salary: "£33,000 - £35,000",
      description: "The Software Test Analyst is responsible for ensuring the quality, reliability, and performance of software applications and systems developed by PureCode Ltd. Work in a hybrid environment with flexibility to work from home up to 2 days per week.",
      requirements: [
        "Proficiency in Python, C#, and Java programming languages",
        "Strong experience with test automation frameworks and tools",
        "Hands-on experience with AWS and Azure cloud platforms",
        "Solid understanding of DevOps principles and CI/CD pipelines",
        "Knowledge of modern software testing methodologies and QA best practices",
        "Strong analytical and problem-solving skills",
        "High attention to detail and commitment to quality",
        "Effective communication and collaboration skills",
        "Experience with functional, non-functional, and regression testing"
      ]
    },
    {
      title: "Senior Full Stack Developer",
      department: "Engineering",
      type: "Full-time",
      location: "Warrington, UK",
      salary: "£50,000 - £70,000",
      description: "We're looking for an experienced full stack developer to join our growing team. You'll work on cutting-edge projects using modern technologies and help shape the future of our products.",
      requirements: [
        "5+ years of experience in full stack development",
        "Proficiency in React, Node.js, and TypeScript",
        "Experience with cloud platforms (AWS, Azure, or GCP)",
        "Strong understanding of database design and optimization",
        "Excellent problem-solving and communication skills"
      ]
    },
    {
      title: "DevOps Engineer",
      department: "Infrastructure",
      type: "Full-time",
      location: "Warrington, UK",
      salary: "£45,000 - £65,000",
      description: "Join our infrastructure team to build and maintain scalable, reliable systems. You'll work with modern DevOps tools and practices to ensure our applications run smoothly.",
      requirements: [
        "3+ years of DevOps or infrastructure experience",
        "Experience with containerization (Docker, Kubernetes)",
        "Knowledge of CI/CD pipelines and automation",
        "Familiarity with monitoring and logging tools",
        "Strong scripting skills (Python, Bash, or PowerShell)"
      ]
    },
    {
      title: "UI/UX Designer",
      department: "Design",
      type: "Full-time",
      location: "Warrington, UK",
      salary: "£35,000 - £50,000",
      description: "Create beautiful, intuitive user experiences for our digital products. You'll work closely with our development team to bring designs to life.",
      requirements: [
        "3+ years of UI/UX design experience",
        "Proficiency in design tools (Figma, Sketch, Adobe Creative Suite)",
        "Strong portfolio demonstrating design thinking",
        "Understanding of web and mobile design principles",
        "Experience with user research and testing"
      ]
    },
    {
      title: "Software Development Intern",
      department: "Engineering",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Perfect opportunity for students to gain hands-on experience in software development. Work on real projects and learn from experienced developers.",
      requirements: [
        "Currently studying Computer Science or related field",
        "Basic knowledge of programming languages (Java, Python, JavaScript)",
        "Eagerness to learn and grow",
        "Good communication skills",
        "Available for minimum 3 months"
      ]
    },
    {
      title: "AI/ML Research Intern",
      department: "Research & Development",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Explore cutting-edge AI and machine learning technologies. Contribute to research projects and help develop innovative solutions.",
      requirements: [
        "Studying AI, Machine Learning, Data Science or related field",
        "Knowledge of Python and ML libraries (TensorFlow, PyTorch)",
        "Understanding of statistical concepts",
        "Research mindset and analytical thinking",
        "Available for minimum 3 months"
      ]
    },
    {
      title: "Cybersecurity Intern",
      department: "Security",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Learn about cybersecurity practices and help protect our systems. Gain experience in security assessment and incident response.",
      requirements: [
        "Studying Cybersecurity, Computer Science or related field",
        "Basic understanding of network security concepts",
        "Interest in ethical hacking and security tools",
        "Detail-oriented and security-conscious mindset",
        "Available for minimum 3 months"
      ]
    },
    {
      title: "Data Analytics Intern",
      department: "Data Science",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Work with large datasets to extract meaningful insights. Learn data visualization and statistical analysis techniques.",
      requirements: [
        "Studying Data Science, Statistics, Mathematics or related field",
        "Knowledge of SQL and data analysis tools (Python, R)",
        "Understanding of statistical concepts",
        "Experience with data visualization tools (Tableau, Power BI)",
        "Available for minimum 3 months"
      ]
    },
    {
      title: "Digital Marketing Intern",
      department: "Marketing",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Support our digital marketing efforts across various channels. Learn about SEO, social media marketing, and content creation.",
      requirements: [
        "Studying Marketing, Communications or related field",
        "Understanding of social media platforms",
        "Basic knowledge of SEO and content marketing",
        "Creative thinking and writing skills",
        "Available for minimum 3 months"
      ]
    },
    {
      title: "Business Analysis Intern",
      department: "Business Development",
      type: "Unpaid Internship",
      location: "Warrington, UK",
      salary: "Unpaid",
      description: "Analyze business processes and help identify improvement opportunities. Learn about project management and business strategy.",
      requirements: [
        "Studying Business, Economics or related field",
        "Analytical and problem-solving skills",
        "Understanding of business processes",
        "Good communication and presentation skills",
        "Available for minimum 3 months"
      ]
    }
  ];

  const handleApplyClick = (jobTitle: string, department: string) => {
    setSelectedJob({ title: jobTitle, department });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://public.readdy.ai/ai/img_res/e478b7f8d48cf78e173ab97ac6047e0e.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-6xl mb-6">
            Join Our Team
          </h1>
          <p className="text-xl leading-8 text-teal-100 max-w-3xl mx-auto">
            Be part of a boutique IT consultancy that's making a real impact in the fintech industry. 
            We're looking for passionate professionals to join our growing team.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Work With Us?</h2>
            <p className="text-lg text-gray-600">Discover what makes PURECODE a great place to work</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Collaborative Culture</h3>
              <p className="text-gray-600">Work in a supportive environment where every voice matters and collaboration drives innovation.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Growth Opportunities</h3>
              <p className="text-gray-600">Advance your career with continuous learning opportunities and challenging projects.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation Focus</h3>
              <p className="text-gray-600">Work with cutting-edge technologies and contribute to innovative solutions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-lg text-gray-600">Find your next career opportunity with us</p>
          </div>
          
          <div className="space-y-6">
            {jobs.map((job, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 mr-3">{job.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        job.type === 'Unpaid Internship' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {job.type}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center text-sm text-gray-600 mb-3">
                      <span className="flex items-center mr-4 mb-1">
                        <i className="ri-building-line mr-1"></i>
                        {job.department}
                      </span>
                      <span className="flex items-center mr-4 mb-1">
                        <i className="ri-map-pin-line mr-1"></i>
                        {job.location}
                      </span>
                      <span className="flex items-center mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {job.salary}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{job.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Requirements:</h4>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {job.requirements.map((req, reqIndex) => (
                          <li key={reqIndex}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="lg:ml-6 mt-4 lg:mt-0">
                    <button
                      onClick={() => handleApplyClick(job.title, job.department)}
                      className="w-full lg:w-auto px-6 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 cursor-pointer transition-colors duration-200 whitespace-nowrap"
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits & Perks</h2>
            <p className="text-lg text-gray-600">We take care of our team members</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="ri-heart-pulse-line text-xl text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Health Insurance</h3>
              <p className="text-sm text-gray-600">Comprehensive health coverage</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-xl text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Flexible Hours</h3>
              <p className="text-sm text-gray-600">Work-life balance priority</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="ri-home-wifi-line text-xl text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Remote Work</h3>
              <p className="text-sm text-gray-600">Hybrid working options</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="ri-graduation-cap-line text-xl text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Learning Budget</h3>
              <p className="text-sm text-gray-600">Professional development</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Job Application Modal */}
      {selectedJob && (
        <JobApplicationModal
          isOpen={!!selectedJob}
          onClose={() => setSelectedJob(null)}
          jobTitle={selectedJob.title}
          department={selectedJob.department}
        />
      )}
    </div>
  );
}
